import React, { useEffect, useState, memo } from 'react'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import { withWebSocket } from '~src/WebSocketContext'

const styles = () => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  circle: {
    width: 15,
    height: 15,
    marginRight: '10px !important',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: 'none',
    zIndex: 2,
    position: 'relative',
  },
  queueBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: '#4A90D9',
    color: 'white',
    fontSize: 8,
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
    zIndex: 3,
  },
  hoverText: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    right: 40,
    backgroundColor: 'white',
    border: '1px solid gray',
    height: 50,
    padding: '0px 8px',
    borderRadius: '4px',
    pointerEvents: 'none',
    fontFamily: 'Roboto',
    fontSize: 16,
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  hookCirclesWrapper: {
    position: 'absolute',
    right: 10,
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.3s ease',
    zIndex: 1,
  },
  hookCirclesExpanded: {
    transform: 'translateX(-24px)',
  },
  hookCircle: {
    width: 11,
    height: 11,
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: 'none',
    flexShrink: 0,
    position: 'relative',
  },
  hookQueueBadge: {
    position: 'absolute',
    top: -3,
    right: -3,
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#4A90D9',
    color: 'white',
    fontSize: 7,
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
    zIndex: 3,
  },
})

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return '#FFD700'
    case 'failed':
      return '#FF0000'
    case 'success':
      return '#32CD32'
    default:
      return '#32CD32'
  }
}

const getStatusText = (label, status, queueCount) => {
  const statusText = {
    pending: 'Executing',
    failed: 'Failed',
    success: 'Success',
  }
  let text = `${label}: ${statusText[status] || status || 'Success'}`
  if (queueCount > 0) text += ` (queue: ${queueCount})`
  return text
}

const ProgressStatusCircle = memo(
  ({
    classes,
    status: initialStatus,
    hookStatus: initialHookStatus,
    queueInfo: initialQueueInfo,
    socket,
    id,
  }) => {
    const [isContainerHovered, setContainerHovered] = useState(false)
    const [hoveredCircle, setHoveredCircle] = useState(null)

    const [mainStatus, setMainStatus] = useState(initialStatus)
    const [hookStatus, setHookStatus] = useState(initialHookStatus)
    const [queueInfo, setQueueInfo] = useState(
      initialQueueInfo || { priorityLength: 0, hookLength: 0 },
    )

    const channel = `updateJobStatus:${id}`

    useEffect(() => {
      if (!socket) return

      const listener = state => {
        if (state.status) setMainStatus(state.status)
        if (state.hookStatus !== undefined) setHookStatus(state.hookStatus)
        if (state.queueInfo) setQueueInfo(state.queueInfo)
      }

      if (!socket.hasListeners(channel)) {
        socket.on(channel, listener)
      }

      return () => {
        socket.off(channel, listener)
      }
    }, [])

    const showHookCircle = !!hookStatus

    return (
      <div
        className={classes.container}
        onMouseEnter={() => setContainerHovered(true)}
        onMouseLeave={() => {
          setContainerHovered(false)
          setHoveredCircle(null)
        }}
      >
        {hoveredCircle && (
          <div className={classes.hoverText}>
            <Typography>
              {hoveredCircle === 'main'
                ? getStatusText('Main', mainStatus, queueInfo.priorityLength)
                : getStatusText('Hook', hookStatus, queueInfo.hookLength)}
            </Typography>
          </div>
        )}

        {showHookCircle && (
          <div
            className={`${classes.hookCirclesWrapper} ${
              isContainerHovered ? classes.hookCirclesExpanded : ''
            }`}
          >
            <div style={{ position: 'relative' }}>
              <Paper
                className={classes.hookCircle}
                style={{ backgroundColor: getStatusColor(hookStatus) }}
                onMouseEnter={() => setHoveredCircle('hook')}
                onMouseLeave={() => setHoveredCircle(null)}
              />
              {queueInfo.hookLength > 0 && (
                <span className={classes.hookQueueBadge}>{queueInfo.hookLength}</span>
              )}
            </div>
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <Paper
            className={classes.circle}
            style={{ backgroundColor: getStatusColor(mainStatus) }}
            onMouseEnter={() => setHoveredCircle('main')}
            onMouseLeave={() => setHoveredCircle(null)}
          />
          {queueInfo.priorityLength > 0 && (
            <span className={classes.queueBadge}>{queueInfo.priorityLength}</span>
          )}
        </div>
      </div>
    )
  },
)

export default withWebSocket(withStyles(styles)(ProgressStatusCircle))
